import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ContactForm } from '../components/ContactForm';
import { mockData } from '../data/mock';
import { 
  PhoneCall, TrendingUp, RotateCcw, Users, CheckCircle2, 
  ArrowRight, Trophy, Smile, Sparkles, Flag, Activity,
  Target, BarChart3, Zap, Menu, X, Plus
} from 'lucide-react';

const iconMap = {
  'phone-call': PhoneCall,
  'trending-up': TrendingUp,
  'rotate-ccw': RotateCcw,
  'users': Users,
  'trophy': Trophy,
  'smile': Smile,
  'sparkles': Sparkles,
  'flag': Flag,
  'activity': Activity,
  'plus': Plus
};

export const Landing = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formType, setFormType] = useState('demo');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const openForm = (type) => {
    setFormType(type);
    setIsFormOpen(true);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <PhoneCall className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900">ClyniCall</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#solution" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Solution</a>
              <a href="#results" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Results</a>
              <a href="#industries" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Industries</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">FAQ</a>
              <Button onClick={() => openForm('demo')} className="bg-blue-600 hover:bg-blue-700 text-white">
                Book a Demo
              </Button>
            </nav>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#solution" className="block text-gray-700 hover:text-blue-600 font-medium">Solution</a>
              <a href="#results" className="block text-gray-700 hover:text-blue-600 font-medium">Results</a>
              <a href="#industries" className="block text-gray-700 hover:text-blue-600 font-medium">Industries</a>
              <a href="#faq" className="block text-gray-700 hover:text-blue-600 font-medium">FAQ</a>
              <Button onClick={() => openForm('demo')} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                Book a Demo
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-20 md:py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {mockData.hero.headline}
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-10 leading-relaxed">
              {mockData.hero.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                onClick={() => openForm('demo')}
                className="bg-blue-600 hover:bg-blue-700 text-white h-14 px-8 text-lg font-semibold"
              >
                Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => openForm('audit')}
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 h-14 px-8 text-lg font-semibold"
              >
                Get a Revenue Audit
              </Button>
            </div>
            <p className="text-gray-600 font-medium">{mockData.hero.trustStrip}</p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {mockData.problems.map((problem, index) => (
                <Card key={index} className="border-2 border-red-100 bg-red-50/30 hover:shadow-lg transition-shadow">
                  <CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-red-700 mb-3">{problem.title}</h3>
                    <p className="text-gray-700">{problem.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="text-center bg-red-600 text-white py-8 px-6 rounded-xl">
              <p className="text-2xl md:text-3xl font-bold">{mockData.lostRevenue}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
              {mockData.solution.title}
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-16"></div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {mockData.solution.pillars.map((pillar, index) => {
                const Icon = iconMap[pillar.icon];
                return (
                  <Card key={index} className="border-2 border-blue-100 hover:border-blue-300 hover:shadow-xl transition-all">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{pillar.title}</h3>
                      <p className="text-gray-600">{pillar.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="results" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
              Real Revenue Impact
            </h2>
            <p className="text-xl text-center text-gray-600 mb-12">(Illustrative Examples)</p>
            
            <Tabs defaultValue="Padel Centre" className="w-full">
              <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full mb-8 h-auto">
                {mockData.caseStudies.map((study) => {
                  const Icon = iconMap[study.icon];
                  return (
                    <TabsTrigger 
                      key={study.industry} 
                      value={study.industry}
                      className="flex items-center gap-2 py-3 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{study.industry}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              
              {mockData.caseStudies.map((study) => (
                <TabsContent key={study.industry} value={study.industry}>
                  <Card className="border-2 border-blue-100">
                    <CardContent className="p-8 md:p-12">
                      <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div>
                          <h3 className="text-2xl font-bold text-red-700 mb-4">Before ClyniCall:</h3>
                          <ul className="space-y-3">
                            {study.before.points.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-gray-700">
                                <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h3 className="text-2xl font-bold text-green-700 mb-4">After:</h3>
                          <ul className="space-y-3">
                            {study.after.points.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-gray-700">
                                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-xl">
                        <h3 className="text-2xl font-bold mb-4">Result:</h3>
                        <ul className="space-y-3">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-center gap-3 text-lg">
                              <Zap className="w-6 h-6 text-yellow-300" />
                              <span className="font-semibold">{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
            
            <p className="text-center text-sm text-gray-500 mt-8 italic">
              Results are illustrative examples based on typical client outcomes and will vary by business.
            </p>
          </div>
        </div>
      </section>

      {/* ROI Metrics Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-12">
              {mockData.metrics.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="text-5xl md:text-6xl font-bold mb-3">{metric.stat}</div>
                  <div className="text-xl text-blue-100">{metric.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">How It Works</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {mockData.howItWorks.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-blue-600 text-white rounded-full flex items-center justify-center text-3xl font-bold mb-6">
                      {step.step}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg">{step.description}</p>
                  </div>
                  {index < mockData.howItWorks.length - 1 && (
                    <ArrowRight className="hidden md:block absolute top-10 -right-4 w-8 h-8 text-blue-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{mockData.differentiation.headline}</h2>
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {mockData.differentiation.points.map((point, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/10 p-6 rounded-xl backdrop-blur-sm">
                  <CheckCircle2 className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <span className="text-lg font-medium text-left">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
              Built for Your Industry
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-16"></div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {mockData.industries.map((industry, index) => {
                const Icon = iconMap[industry.icon];
                return (
                  <Card key={index} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                    <CardContent className="p-8">
                      <Icon className="w-12 h-12 text-blue-600 mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{industry.name}</h3>
                      <p className="text-gray-600 leading-relaxed">{industry.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-16"></div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border-2 border-gray-200 rounded-xl px-6 hover:border-blue-300 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-600 py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-bold mb-8">
              Stop losing revenue from missed calls.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => openForm('demo')}
                className="bg-white text-blue-600 hover:bg-gray-100 h-14 px-8 text-lg font-semibold"
              >
                Book a Demo <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => openForm('audit')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-14 px-8 text-lg font-semibold"
              >
                Get a Revenue Audit
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <PhoneCall className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">ClyniCall</span>
              </div>
              <p className="text-gray-400">
                Transforming phone lines into revenue engines for premium service businesses.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#solution" className="hover:text-blue-400 transition-colors">Solution</a></li>
                <li><a href="#results" className="hover:text-blue-400 transition-colors">Results</a></li>
                <li><a href="#industries" className="hover:text-blue-400 transition-colors">Industries</a></li>
                <li><a href="#faq" className="hover:text-blue-400 transition-colors">FAQ</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>&copy; 2024 ClyniCall. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <ContactForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} formType={formType} />
    </div>
  );
};
